import React from "react";
import { useTranslation } from "react-i18next";
import { Book, Target, Flag, Video, BarChart, MapPin, User, Users } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion } from "@/components/ui/accordion";
import ManualSection, { ManualSectionProps } from "./ManualSection";

const UserManual = () => {
  const { t } = useTranslation('common');
  const manualSections: ManualSectionProps[] = [
    {
      id: "getting-started",
      title: t('help.userManual.sections.gettingStarted.title'),
      icon: <User className="h-5 w-5" />,
      content: (
        <div className="space-y-4">
          <p>{t('help.userManual.sections.gettingStarted.intro')}</p>
          <div className="space-y-2">
            <h4 className="font-semibold">{t('help.userManual.sections.gettingStarted.setupTitle')}</h4>
            <ul className="list-disc list-inside space-y-1 text-sm">
              {(t('help.userManual.sections.gettingStarted.setupItems', { returnObjects: true }) as string[]).map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </div>
        </div>
      )
    },
    {
      id: "pelz-tests",
      title: t('help.userManual.sections.pelzTests.title'),
      icon: <Target className="h-5 w-5" />,
      content: (
        <div className="space-y-4">
          <p>{t('help.userManual.sections.pelzTests.intro')}</p>
          <div className="space-y-3">
            <div>
              <h4 className="font-semibold">{t('help.userManual.sections.pelzTests.puttingTitle')}</h4>
              <ul className="list-disc list-inside space-y-1 text-sm mt-2">
                {(t('help.userManual.sections.pelzTests.puttingItems', { returnObjects: true }) as string[]).map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-semibold">{t('help.userManual.sections.pelzTests.approachTitle')}</h4>
              <ul className="list-disc list-inside space-y-1 text-sm mt-2">
                {(t('help.userManual.sections.pelzTests.approachItems', { returnObjects: true }) as string[]).map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )
    },
    {
      id: "training-levels",
      title: t('help.userManual.sections.trainingLevels.title'),
      icon: <Flag className="h-5 w-5" />,
      content: (
        <div className="space-y-4">
          <p>{t('help.userManual.sections.trainingLevels.intro')}</p>
          <div className="space-y-3">
            <div>
              <h4 className="font-semibold text-green-600">{t('help.userManual.sections.trainingLevels.level3Title')}</h4>
              <p className="text-sm">{t('help.userManual.sections.trainingLevels.level3Desc')}</p>
            </div>
            <div>
              <h4 className="font-semibold text-blue-600">{t('help.userManual.sections.trainingLevels.level2Title')}</h4>
              <p className="text-sm">{t('help.userManual.sections.trainingLevels.level2Desc')}</p>
            </div>
            <div>
              <h4 className="font-semibold text-orange-600">{t('help.userManual.sections.trainingLevels.level1Title')}</h4>
              <p className="text-sm">{t('help.userManual.sections.trainingLevels.level1Desc')}</p>
            </div>
          </div>
          <div className="bg-blue-50 p-3 rounded-md">
            <p className="text-sm text-blue-800">
              <strong>{t('common.info')}:</strong> {t('help.userManual.sections.trainingLevels.tip')}
            </p>
          </div>
        </div>
      )
    },
    {
      id: "scoring-system",
      title: t('help.userManual.sections.scoringSystem.title'),
      icon: <BarChart className="h-5 w-5" />,
      content: (
        <div className="space-y-4">
          <p>{t('help.userManual.sections.scoringSystem.intro')}</p>
          <div className="space-y-3">
            <div>
              <h4 className="font-semibold">{t('help.userManual.sections.scoringSystem.puttingTitle')}</h4>
              <ul className="list-disc list-inside space-y-1 text-sm">
                {(t('help.userManual.sections.scoringSystem.puttingItems', { returnObjects: true }) as string[]).map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-semibold">{t('help.userManual.sections.scoringSystem.approachTitle')}</h4>
              <ul className="list-disc list-inside space-y-1 text-sm">
                {(t('help.userManual.sections.scoringSystem.approachItems', { returnObjects: true }) as string[]).map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )
    },
    {
      id: "distance-tracking",
      title: t('help.userManual.sections.distanceTracking.title'),
      icon: <MapPin className="h-5 w-5" />,
      content: (
        <div className="space-y-4">
          <p>{t('help.userManual.sections.distanceTracking.intro')}</p>
          <div className="space-y-2">
            <h4 className="font-semibold">{t('help.userManual.sections.distanceTracking.howToTitle')}</h4>
            <p className="text-sm">{t('help.userManual.sections.distanceTracking.howToIntro')}</p>
            <ul className="list-disc list-inside space-y-1 text-sm">
              {(t('help.userManual.sections.distanceTracking.howToItems', { returnObjects: true }) as string[]).map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </div>
          <div className="bg-yellow-50 p-3 rounded-md">
            <p className="text-sm text-yellow-800">
              <strong>{t('common.info')}:</strong> {t('help.userManual.sections.distanceTracking.note')}
            </p>
          </div>
        </div>
      )
    },
    {
      id: "results-analysis",
      title: t('help.userManual.sections.resultsAnalysis.title'),
      icon: <BarChart className="h-5 w-5" />,
      content: (
        <div className="space-y-4">
          <p>{t('help.userManual.sections.resultsAnalysis.intro')}</p>
          <div className="space-y-3">
            <div>
              <h4 className="font-semibold">{t('help.userManual.sections.resultsAnalysis.chartsTitle')}</h4>
              <ul className="list-disc list-inside space-y-1 text-sm">
                {(t('help.userManual.sections.resultsAnalysis.chartsItems', { returnObjects: true }) as string[]).map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-semibold">{t('help.userManual.sections.resultsAnalysis.historyTitle')}</h4>
              <ul className="list-disc list-inside space-y-1 text-sm">
                {(t('help.userManual.sections.resultsAnalysis.historyItems', { returnObjects: true }) as string[]).map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )
    },
    {
      id: "videos",
      title: t('help.userManual.sections.videos.title'),
      icon: <Video className="h-5 w-5" />,
      content: (
        <div className="space-y-4">
          <p>{t('help.userManual.sections.videos.intro')}</p>
          <div className="space-y-2">
            <h4 className="font-semibold">{t('help.userManual.sections.videos.featuresTitle')}</h4>
            <ul className="list-disc list-inside space-y-1 text-sm">
              {(t('help.userManual.sections.videos.featuresItems', { returnObjects: true }) as string[]).map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </div>
        </div>
      )
    },
    {
      id: "supervision",
      title: t('help.userManual.sections.supervision.title'),
      icon: <Users className="h-5 w-5" />,
      content: (
        <div className="space-y-4">
          <p>{t('help.userManual.sections.supervision.intro')}</p>
          <div className="space-y-3">
            <div>
              <h4 className="font-semibold">{t('help.userManual.sections.supervision.coachTitle')}</h4>
              <ul className="list-disc list-inside space-y-1 text-sm">
                {(t('help.userManual.sections.supervision.coachItems', { returnObjects: true }) as string[]).map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-semibold">{t('help.userManual.sections.supervision.traineeTitle')}</h4>
              <ul className="list-disc list-inside space-y-1 text-sm">
                {(t('help.userManual.sections.supervision.traineeItems', { returnObjects: true }) as string[]).map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>
          </div>
          <div className="bg-blue-50 p-3 rounded-md">
            <p className="text-sm text-blue-800">
              <strong>{t('common.info')}:</strong> {t('help.userManual.sections.supervision.important')}
            </p>
          </div>
        </div>
      )
    }
  ];

  return (
    <Card className="mb-8">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Book className="h-6 w-6 text-golf-600" />
          {t('help.userManual.title')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Accordion type="single" collapsible className="w-full">
          {manualSections.map(section => (
            <ManualSection key={section.id} section={section} />
          ))}
        </Accordion>
      </CardContent>
    </Card>
  );
};

export default UserManual;
